
import { useEffect, useState } from 'react';
import { useTranslations } from '@/i18n/translations';
import { useMemberDisplayName } from '../MemberIdentity';
import { RouterLinkAs } from '@/lib/router-link';
import { useUnsavedChangesGuard } from '@/lib/use-unsaved-changes-guard';
import {
  AlertDialog,
  Body,
  Box,
  Button,
  CalloutBar,
  Caption,
  Card,
  Dialog,
  DialogSection,
  Heading,
  I,
  DropdownMenu,
  DropdownMenuItem,
  Pill,
  Row,
  Stack,
} from '@leanin/ui';
import {
  ReactionButton,
  ReactionSummary,
  type ReactionKind,
} from '../posts/index';

import { MemberRow } from '../MemberRow';
import type { FeedPost } from './types';
import {
  useDeleteFeedPost,
  useDismissFeedPost,
  usePollVote,
  useReactToFeedPost,
  useRemovePostFromGroup,
  useSaveFeedPost,
  useUnsaveFeedPost,
} from '../../lib/mutations/feed';
import { useErrorMessage } from '../../lib/error-message';
import { feedErrorMessage } from './feed-error-copy';
import { postContentSection } from '../../lib/analytics/events';
import { useViewer } from '../../lib/viewer-context';
import { useMember } from '../../lib/use-session';
import { PostComments } from './PostComments';
import { PostCommentPreview } from './PostCommentPreview';
import { PostComposerSheet } from './PostComposer';
import { ReportContentDialog } from '../moderation/ReportContentDialog';
import { RemoveContentDialog } from '../moderation/RemoveContentDialog';
import { LeaderRemovalBanner } from '../moderation/LeaderRemovalBanner';
import { postRemovalTargets, type PostRemovalTarget } from './post-removal-targets';
import { PostEntityCard } from './PostEntityCard';
import { PollCard } from './PollCard';
import { LinkPreviewCard } from '@/components/link-preview';
import { ReactionsModal } from './ReactionsModal';
import { ShareEntityModal } from './ShareEntityModal';
import { PostMediaGallery } from './PostMediaGallery';
import { ExpandableText } from './ExpandableText';
import { RichBodyRenderer } from '../editor';
import styles from './PostCard.module.css';

type Props = {
  post: FeedPost;
  // When set, the scope pill matching this id is filtered out and a "Pinned"
  // indicator is shown if the post is pinned within that scope. Used when the
  // card is rendered inside a group's own detail page — which also makes it
  // the group a leader's "Remove post" acts on.
  hideScopeId?: string;
  // Leader-only Pin/Unpin: offered only when the matching handler is
  // supplied, which the group feed does for its leaders.
  onPin?: () => void;
  onUnpin?: () => void;
  // Leader-only "Send as broadcast": opens the group's broadcast composer
  // prefilled from this post. Only offered when the handler is supplied —
  // same contract as Pin/Unpin above.
  onSendAsBroadcast?: () => void;
  // Platform-admin-only feature/unfeature of the platform-wide featured
  // slot. Only offered when the viewer is a platform admin AND the matching
  // handler is supplied — same contract as Pin/Unpin above.
  onFeature?: () => void;
  onUnfeature?: () => void;
  // Renders the entire body instead of the clamped feed preview. Set on the
  // post detail page; list surfaces keep the default preview.
  fullBody?: boolean;
  // Comment deep links (`/posts/{id}?comment=…`): open the comments section
  // from first render, and scroll/flash the target comment once loaded.
  initialCommentsOpen?: boolean;
  highlightCommentId?: string;
  // Open the comments with the reply composer under this top-level comment
  // (Reply on the feed card's comment preview, carried into the post dialog).
  initialReplyCommentId?: string;
  // `dialog` renders the post as the body of the long-thread post dialog:
  // no card frame of its own, and the comment composer pinned to the
  // dialog's bottom edge while the thread scrolls.
  layout?: 'card' | 'dialog';
  // Told whether the comments hold text the viewer hasn't posted or saved,
  // so the post dialog can ask before a dismissal discards it.
  onUnsentCommentChange?: (unsent: boolean) => void;
  // A dialog-layout card's delete or hide also removes the card it opened
  // from: the parent closes the dialog and drops its own card.
  onRemoved?: () => void;
};

const MAX_VISIBLE_SCOPE_PILLS = 3;

// Feed-preview clamp heights, LinkedIn-style: tighter when media or attached
// entities already give the card visual weight below the text.
const PREVIEW_LINES = 5;
const PREVIEW_LINES_WITH_ATTACHMENTS = 3;

// Past this many comments the thread opens in a post dialog instead of
// expanding inline, so a long thread can't stretch one card down the feed.
const INLINE_COMMENTS_MAX = 10;

function scopeHref(scope: FeedPost['scopes'][number]): string {
  if (scope.groupKind === 'network') return `/networks/${scope.groupId}`;
  if (scope.groupKind === 'space') {
    return `/groups/${scope.groupId}`;
  }
  return `/circles/${scope.groupId}`;
}

function scopeIcon(kind: FeedPost['scopes'][number]['groupKind']) {
  if (kind === 'network') return I.network;
  if (kind === 'space') return I.chat;
  return I.circles;
}

function scopeTone(
  kind: FeedPost['scopes'][number]['groupKind'],
): 'navy' | 'burgundy' | 'muted' {
  if (kind === 'network') return 'navy';
  if (kind === 'space') return 'muted';
  return 'burgundy';
}

// A route change while the post dialog holds an unsent draft — an in-dialog
// link, browser Back, a reload — waits behind the same prompt Escape and the
// X show. Mounted only while the dialog is open, so the feed doesn't register
// a navigation blocker per card.
function DialogDraftNavigationGuard({ dirty }: { dirty: boolean }) {
  const t = useTranslations('Feed');
  const guard = useUnsavedChangesGuard({ dirty });
  return (
    <AlertDialog
      open={guard.dialogOpen}
      onOpenChange={(open) => {
        if (!open) guard.reset();
      }}
      title={t('post.discardComment.title')}
      description={t('post.discardComment.description')}
      actionLabel={t('post.discardComment.confirm')}
      cancelLabel={t('post.discardComment.cancel')}
      destructive
      onAction={guard.proceed}
    />
  );
}

function PostScopeRow({
  post,
  hideScopeId,
}: {
  post: FeedPost;
  hideScopeId?: string;
}) {
  const t = useTranslations('Feed');
  if (post.isGlobal) {
    return (
      <Row gap="sm" align="center">
        <Pill icon={I.globe}>{t('post.everyone')}</Pill>
      </Row>
    );
  }
  const scopes =
    hideScopeId !== undefined
      ? post.scopes.filter((s) => s.groupId !== hideScopeId)
      : post.scopes;
  if (scopes.length === 0) return null;
  const visible = scopes.slice(0, MAX_VISIBLE_SCOPE_PILLS);
  const overflow = scopes.length - visible.length;
  return (
    <Row gap="sm" align="center" style={{ flexWrap: 'wrap' }}>
      {visible.map((scope) => (
        <Pill
          key={scope.groupId}
          icon={scopeIcon(scope.groupKind)}
          href={scopeHref(scope)}
          linkAs={RouterLinkAs}
          tone={scopeTone(scope.groupKind)}
        >
          {scope.groupName}
        </Pill>
      ))}
      {overflow > 0 ? (
        <Pill>{t('post.moreCount', { count: overflow })}</Pill>
      ) : null}
    </Row>
  );
}

export const PostCard = ({
  post,
  hideScopeId,
  onPin,
  onUnpin,
  onSendAsBroadcast,
  onFeature,
  onUnfeature,
  fullBody = false,
  initialCommentsOpen = false,
  highlightCommentId,
  initialReplyCommentId,
  layout = 'card',
  onUnsentCommentChange,
  onRemoved,
}: Props) => {
  // Participation is the server's call, not ours — `post.viewerCan` comes
  // straight from the rules `POST /posts/{id}/reactions` and
  // `POST /posts/{id}/comments` enforce. Reaction counts and the comment
  // thread stay visible either way; saving and sharing are read-gated, not
  // participation-gated, so they're always offered.
  const canReact = post.viewerCan.react;
  const canComment = post.viewerCan.comment;
  const t = useTranslations('Feed');
  const describeError = useErrorMessage();
  const viewer = useViewer();
  const isAuthor = post.author.kind === 'member' && post.author.id === viewer.id;
  const authorName = useMemberDisplayName(post.author);
  const isPlatformAdmin = useMember()?.platformRole === 'admin';
  const composerViewer = {
    id: viewer.id,
    name: viewer.displayName,
    ...(viewer.photoUrl !== null ? { photoUrl: viewer.photoUrl } : {}),
  };
  const pinnedInScope =
    hideScopeId !== undefined &&
    post.scopes.some(
      (s) => s.groupId === hideScopeId && s.pinnedAt !== null,
    );
  const contentSection = postContentSection(post);

  const reactMutation = useReactToFeedPost();
  const saveMutation = useSaveFeedPost();
  const pollVote = usePollVote();
  const unsaveMutation = useUnsaveFeedPost();
  const deleteMutation = useDeleteFeedPost();
  const dismissMutation = useDismissFeedPost();
  const removeMutation = useRemovePostFromGroup();

  // Reactions, saved state, body and media render from `post`, which every
  // surface reads from the query cache; the mutations patch that cache. A
  // second card for the same post (the post dialog, the detail page) can't
  // drift from this one.
  const [flashError, setFlashError] = useState<string | null>(null);

  const [showComments, setShowComments] = useState(initialCommentsOpen);
  // A `?comment=` deep link can arrive while this card is already mounted
  // (Back/Forward on the post page — route components never remount on a
  // search-only change). The panel must open for it, not only on mount.
  useEffect(() => {
    if (highlightCommentId !== undefined) setShowComments(true);
  }, [highlightCommentId]);
  // A thread already open inline stays inline when it grows past the cap, so
  // the inline panel and the dialog never show at once.
  const commentsInDialog =
    !fullBody && !showComments && post.comments > INLINE_COMMENTS_MAX;
  const [postDialogOpen, setPostDialogOpen] = useState(false);
  const [dialogCommentUnsent, setDialogCommentUnsent] = useState(false);
  const [discardCommentOpen, setDiscardCommentOpen] = useState(false);
  const [replyToCommentId, setReplyToCommentId] = useState<string | null>(
    initialReplyCommentId ?? null,
  );
  const openComments = (replyTo: string | null) => {
    setReplyToCommentId(replyTo);
    if (commentsInDialog) setPostDialogOpen(true);
    else setShowComments(true);
  };
  const [shareOpen, setShareOpen] = useState(false);
  const [reactionsOpen, setReactionsOpen] = useState(false);
  const [reportOpen, setReportOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [removeTarget, setRemoveTarget] = useState<PostRemovalTarget | null>(null);
  const [deleted, setDeleted] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  const onReact = (kind: ReactionKind) => {
    setFlashError(null);
    reactMutation.mutate(
      { postId: post.id, kind, section: contentSection },
      {
        onError: (err) => {
          setFlashError(feedErrorMessage(err, describeError, t, 'post.errors.react'));
        },
      },
    );
  };

  const onToggleSave = () => {
    setFlashError(null);
    const onError = (err: unknown) => {
      setFlashError(feedErrorMessage(err, describeError, t, 'post.errors.save'));
    };
    if (!post.viewerSaved) {
      saveMutation.mutate(
        { postId: post.id, section: contentSection },
        { onError },
      );
    } else {
      unsaveMutation.mutate(post.id, { onError });
    }
  };

  const onConfirmDelete = () => {
    setDeleteOpen(false);
    deleteMutation.mutate(post.id, {
      onSuccess: () => {
        setDeleted(true);
        onRemoved?.();
      },
      onError: (err) => {
        setFlashError(feedErrorMessage(err, describeError, t, 'post.errors.delete'));
      },
    });
  };

  if (deleted || dismissed) return null;

  const onDismiss = () => {
    // Optimistically hide; the server upsert is idempotent, so a failure
    // case (we still show toast / error) can be rolled back. Most users
    // won't see the post again either way.
    setDismissed(true);
    dismissMutation.mutate(
      { postId: post.id },
      {
        onSuccess: () => onRemoved?.(),
        onError: (err) => {
          setDismissed(false);
          setFlashError(feedErrorMessage(err, describeError, t, 'post.errors.hide'));
        },
      },
    );
  };

  // Leaders remove a post from the group they lead (the server's
  // `viewerCan.removeFrom`); only the author deletes it outright.
  const removalTargets = postRemovalTargets(post, hideScopeId);
  // A comment leaves the thread as a whole, so its removal names one of
  // those groups: this feed's own group when removable, else the first.
  const commentRemovalGroup = removalTargets[0];
  const removedFromGroupIds = new Set(post.removals.map((r) => r.groupId));
  const stillVisibleIn = post.scopes
    .filter((s) => !removedFromGroupIds.has(s.groupId))
    .map((s) => s.groupName);
  // Removed from every group it was shared to: the card recedes and loses
  // its engagement line, since nobody but the author can reach it now. A
  // post still live elsewhere keeps its full treatment; the banner says
  // which group it left.
  const removedEverywhere = post.removals.length > 0 && stillVisibleIn.length === 0;

  return (
    <Card
      pad="flush"
      {...(layout === 'dialog'
        ? // Unclipped: an overflow boundary here would stop the pinned
          // composer from sticking to the dialog's scroll edge.
          { bordered: false, clip: false }
        : { style: { overflow: 'hidden' } })}
    >
      {post.featuredAt !== null ? (
        <CalloutBar tone="poppy" icon={I.spark} label={t('post.featured')} />
      ) : null}
      {post.recommended !== undefined ? (
        <CalloutBar
          tone="neutral"
          icon={I.spark}
          label={t('post.recommended')}
          meta={post.recommended.reason}
        />
      ) : null}
      {pinnedInScope ? (
        <CalloutBar tone="poppy" icon={I.pin} label={t('post.pinnedByLeaders')} />
      ) : null}
      {post.removals.map((removal) => (
        <LeaderRemovalBanner
          key={removal.groupId}
          removal={removal}
          contentKind="post"
          stillVisibleIn={stillVisibleIn}
        />
      ))}

      <Stack
        gap="md"
        {...(layout === 'dialog' ? { py: 'lg' as const } : { p: 'lg' as const })}
        className={[
          layout === 'dialog' ? styles.dialogInset : null,
          removedEverywhere ? styles.removed : null,
        ]
          .filter((c) => c !== null)
          .join(' ')}
      >
        <MemberRow
          member={post.author}
          nameFont="var(--type-title-sm)"
          {...(post.author.kind === 'member' && post.author.role.length > 0
            ? { sublabel: post.author.role }
            : {})}
          meta={<Caption>{post.time}</Caption>}
          avatarSize={44}
          right={
            <DropdownMenu
              trigger={
                <Button variant="ghost" size="sm" icon={I.more} aria-label={t('post.moreActionsAria')} />
              }
            >
              <DropdownMenuItem onSelect={() => setShareOpen(true)}>
                {t('post.menu.share')}
              </DropdownMenuItem>
              <DropdownMenuItem onSelect={onToggleSave}>
                {post.viewerSaved ? t('post.menu.saved') : t('post.menu.save')}
              </DropdownMenuItem>
              {isAuthor ? (
                <DropdownMenuItem onSelect={() => setEditOpen(true)}>
                  {t('post.menu.edit')}
                </DropdownMenuItem>
              ) : null}
              {onUnpin !== undefined && pinnedInScope ? (
                <DropdownMenuItem onSelect={onUnpin}>{t('post.menu.unpin')}</DropdownMenuItem>
              ) : null}
              {onPin !== undefined && !pinnedInScope ? (
                <DropdownMenuItem onSelect={onPin}>{t('post.menu.pin')}</DropdownMenuItem>
              ) : null}
              {onSendAsBroadcast !== undefined ? (
                <DropdownMenuItem onSelect={onSendAsBroadcast}>
                  {t('post.menu.sendAsBroadcast')}
                </DropdownMenuItem>
              ) : null}
              {isPlatformAdmin && onUnfeature !== undefined && post.featuredAt !== null ? (
                <DropdownMenuItem onSelect={onUnfeature}>
                  {t('post.menu.unfeature')}
                </DropdownMenuItem>
              ) : null}
              {isPlatformAdmin && onFeature !== undefined && post.featuredAt === null ? (
                <DropdownMenuItem onSelect={onFeature}>
                  {t('post.menu.feature')}
                </DropdownMenuItem>
              ) : null}
              {!isAuthor ? (
                <DropdownMenuItem onSelect={onDismiss}>
                  {t('post.menu.hide')}
                </DropdownMenuItem>
              ) : null}
              {!isAuthor ? (
                <DropdownMenuItem onSelect={() => setReportOpen(true)}>
                  {t('post.menu.report')}
                </DropdownMenuItem>
              ) : null}
              {removalTargets.map((target) => (
                <DropdownMenuItem
                  key={target.groupId}
                  destructive
                  onSelect={() => setRemoveTarget(target)}
                >
                  {removalTargets.length > 1 && target.groupName !== null
                    ? t('post.menu.removeFromNamed', { group: target.groupName })
                    : t('post.menu.removeFrom', { kind: target.groupKind })}
                </DropdownMenuItem>
              ))}
              {isAuthor ? (
                <DropdownMenuItem destructive onSelect={() => setDeleteOpen(true)}>
                  {t('post.menu.delete')}
                </DropdownMenuItem>
              ) : null}
            </DropdownMenu>
          }
        />

        <PostScopeRow
          post={post}
          {...(hideScopeId !== undefined ? { hideScopeId } : {})}
        />

        {post.title !== undefined ? (
          <Heading level={3}>
            {post.title}
          </Heading>
        ) : null}

        {editOpen ? (
          <PostComposerSheet
            viewer={composerViewer}
            mode="edit"
            editPostId={post.id}
            initialBody={post.body}
            initialBodyFormat={post.bodyFormat}
            initialMentions={post.mentions}
            initialMedia={post.media}
            initialAttached={post.attachedEntities}
            chrome="flush"
            onClose={() => setEditOpen(false)}
          />
        ) : fullBody ? (
          <RichBodyRenderer bodyFormat={post.bodyFormat} mentions={post.mentions}>
            {post.body}
          </RichBodyRenderer>
        ) : (
          <ExpandableText
            lines={
              post.media.length > 0 || post.attachedEntities.length > 0
                ? PREVIEW_LINES_WITH_ATTACHMENTS
                : PREVIEW_LINES
            }
            moreLabel={t('post.seeMore')}
          >
            <RichBodyRenderer bodyFormat={post.bodyFormat} mentions={post.mentions}>
              {post.body}
            </RichBodyRenderer>
          </ExpandableText>
        )}

        {post.attachedEntities.length > 0 ? (
          <div
            style={{
              display: 'grid',
              // eslint-disable-next-line @leanin/ui/no-inline-spacing-vars -- CSS grid layout; no Box prop for grid containers
              gap: 'var(--space-sm)',
              gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
            }}
          >
            {post.attachedEntities.map((entity, idx) => (
              <PostEntityCard key={`${entity.kind}:${entity.id}:${idx}`} entity={entity} />
            ))}
          </div>
        ) : null}

        {post.linkPreviews.length > 0 ? (
          <div
            style={{
              display: 'grid',
              // eslint-disable-next-line @leanin/ui/no-inline-spacing-vars -- CSS grid layout; no Box prop for grid containers
              gap: 'var(--space-sm)',
              gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
            }}
          >
            {post.linkPreviews.map((preview, idx) => (
              <LinkPreviewCard key={`${preview.url}:${idx}`} preview={preview} />
            ))}
          </div>
        ) : null}

        {!editOpen && post.media.length > 0 ? (
          <PostMediaGallery items={post.media} />
        ) : null}

        {post.poll ? (
          <PollCard
            postId={post.id}
            poll={post.poll}
            question={post.body}
            pending={pollVote.isPending}
            onVote={(optionId) =>
              pollVote.mutate({
                postId: post.id,
                optionId,
                section: contentSection,
              })
            }
          />
        ) : null}

        {flashError !== null ? (
          <Body color="destructive">
            {flashError}
          </Body>
        ) : null}
      </Stack>

      {/* Engagement line — replaces the old chips row; Share and Save moved
          into the header kebab. Left cluster = actions (react, comment);
          right = the reaction discs, the who-reacted roster trigger. */}
      {removedEverywhere ? null : (
      <Row
        py="md"
        {...(layout === 'dialog'
          ? { className: styles.dialogDivided }
          : { mx: 'lg' as const })}
        align="center"
        justify="space-between"
        style={{ borderTop: '1px solid var(--border)' }}
      >
        <Row
          // The ghost buttons carry their own padding, so a tight gap keeps
          // React and Comment reading as one pair.
          gap="xs"
          align="center"
        >
          <ReactionButton
            variant="chip"
            target="post"
            // The React icon lines up with the avatar and text above,
            // mirroring the reaction discs on the right.
            flush="start"
            mine={post.viewerReaction}
            onReact={onReact}
            disabled={!canReact}
          />
          <Button
            variant="ghost"
            size="sm"
            icon={I.chat}
            onClick={() => {
              if (commentsInDialog) {
                openComments(null);
                return;
              }
              setReplyToCommentId(null);
              setShowComments((s) => !s);
            }}
            padX="sm"
            {...(commentsInDialog
              ? { 'aria-haspopup': 'dialog' as const }
              : { 'aria-expanded': showComments })}
            aria-label={t('actions.comment')}
          >
            {t('actions.commentCount', { count: post.comments })}
          </Button>
        </Row>
        <ReactionSummary
          variant="row"
          counts={post.reactionCounts}
          mine={post.viewerReaction}
          onOpenRoster={() => setReactionsOpen(true)}
        />
      </Row>
      )}

      {/* One comment under the engagement line while the panel is closed;
          the detail page always shows the full thread instead. */}
      {!fullBody && !showComments && post.previewComment !== null ? (
        <Box py="lg" mx="lg" style={{ borderTop: 'var(--border-hairline) solid var(--border)' }}>
          <PostCommentPreview
            postId={post.id}
            comment={post.previewComment}
            commentCount={post.comments}
            {...(!post.isGlobal && post.scopes.length === 1
              ? { audienceGroupId: post.scopes[0]!.groupId }
              : {})}
            canReact={canReact}
            canComment={canComment}
            {...(commentRemovalGroup !== undefined
              ? { removalGroup: commentRemovalGroup }
              : {})}
            onViewMore={() => openComments(null)}
            onReply={(commentId) => openComments(commentId)}
          />
        </Box>
      ) : null}

      {showComments ? (
        <Box
          {...(layout === 'dialog'
            ? { pt: 'lg' as const, className: styles.dialogDivided }
            : { py: 'lg' as const, mx: 'lg' as const })}
          style={{
            borderTop: '1px solid var(--border)',
          }}
        >
          <PostComments
            postId={post.id}
            postAuthor={post.author}
            section={contentSection}
            canComment={canComment}
            canReact={canReact}
            {...(commentRemovalGroup !== undefined
              ? { removalGroup: commentRemovalGroup }
              : {})}
            pinComposer={layout === 'dialog'}
            {...(onUnsentCommentChange !== undefined
              ? { onUnsentChange: onUnsentCommentChange }
              : {})}
            {...(replyToCommentId !== null
              ? { initialReplyCommentId: replyToCommentId }
              : {})}
            {...(highlightCommentId !== undefined
              ? { highlightCommentId }
              : {})}
            {...(!post.isGlobal && post.scopes.length === 1
              ? {
                  audienceGroupId: post.scopes[0]!.groupId,
                  audienceGroupName: post.scopes[0]!.groupName,
                }
              : {})}
          />
        </Box>
      ) : null}

      {postDialogOpen ? (
        <Dialog
          open={postDialogOpen}
          onOpenChange={(open) => {
            // Escape, an outside click, and the X all land here: ask first
            // rather than drop a comment the viewer hasn't posted.
            if (!open && dialogCommentUnsent) {
              setDiscardCommentOpen(true);
              return;
            }
            setPostDialogOpen(open);
          }}
          size="lg"
          className={styles.dialog}
          title={t('post.dialogTitle', { name: authorName })}
          titleAlign="center"
          // Opened by Reply: the reply box under that comment takes focus
          // (and scrolls itself into view), not the dialog's first control.
          {...(replyToCommentId !== null
            ? { onOpenAutoFocus: (e: Event) => e.preventDefault() }
            : {})}
        >
          <DialogSection scrollable bleed>
            <PostCard
              post={post}
              layout="dialog"
              fullBody
              initialCommentsOpen
              onUnsentCommentChange={setDialogCommentUnsent}
              onRemoved={() => {
                setPostDialogOpen(false);
                setDeleted(true);
              }}
              {...(replyToCommentId !== null
                ? { initialReplyCommentId: replyToCommentId }
                : {})}
              {...(hideScopeId !== undefined ? { hideScopeId } : {})}
              {...(onPin !== undefined ? { onPin } : {})}
              {...(onUnpin !== undefined ? { onUnpin } : {})}
              {...(onSendAsBroadcast !== undefined ? { onSendAsBroadcast } : {})}
              {...(onFeature !== undefined ? { onFeature } : {})}
              {...(onUnfeature !== undefined ? { onUnfeature } : {})}
            />
          </DialogSection>
        </Dialog>
      ) : null}
      {postDialogOpen ? <DialogDraftNavigationGuard dirty={dialogCommentUnsent} /> : null}

      <ReportContentDialog
        isOpen={reportOpen}
        onOpenChange={setReportOpen}
        targetKind="post"
        targetId={post.id}
        {...(post.author.kind === 'member' ? { subjectName: authorName } : {})}
      />

      {removeTarget !== null ? (
        <RemoveContentDialog
          isOpen
          onOpenChange={(open) => {
            if (!open) setRemoveTarget(null);
          }}
          target={{
            kind: 'post',
            groupName: removeTarget.groupName,
            groupKind: removeTarget.groupKind,
          }}
          author={
            post.author.kind === 'member'
              ? { kind: 'member', name: authorName }
              : { kind: 'deleted' }
          }
          submit={async (input) => {
            await removeMutation.mutateAsync({
              postId: post.id,
              groupId: removeTarget.groupId,
              ...input,
            });
          }}
          onRemoved={() => {
            // Inside the group it left, the card leaves with it; anywhere
            // else the cache patch drops that group's pill and the list
            // refetch decides whether the post still belongs there.
            if (removeTarget.groupId === hideScopeId) {
              setDeleted(true);
              onRemoved?.();
            }
          }}
        />
      ) : null}

      {/* Mount-gated like ShareEntityButton's modal — no resolver query or
          roster fetch until the menu item / pill is actually used. */}
      {shareOpen ? (
        <ShareEntityModal
          open={shareOpen}
          onOpenChange={setShareOpen}
          entity={{ kind: 'post', id: post.id }}
        />
      ) : null}
      {reactionsOpen ? (
        <ReactionsModal
          target={{ kind: 'post', id: post.id }}
          counts={post.reactionCounts}
          open={reactionsOpen}
          onOpenChange={setReactionsOpen}
        />
      ) : null}

      <AlertDialog
        open={discardCommentOpen}
        onOpenChange={setDiscardCommentOpen}
        title={t('post.discardComment.title')}
        description={t('post.discardComment.description')}
        actionLabel={t('post.discardComment.confirm')}
        cancelLabel={t('post.discardComment.cancel')}
        destructive
        onAction={() => {
          setDiscardCommentOpen(false);
          setPostDialogOpen(false);
        }}
      />

      <AlertDialog
        open={deleteOpen}
        onOpenChange={setDeleteOpen}
        title={t('post.deleteDialog.title')}
        description={t('post.deleteDialog.description')}
        actionLabel={t('post.deleteDialog.confirm')}
        cancelLabel={t('post.deleteDialog.cancel')}
        destructive
        onAction={onConfirmDelete}
      />
    </Card>
  );
};
